import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * Custom hook for countdown timer functionality.
 *
 * @param {Object} options - Timer configuration
 * @param {number} options.timeRemaining - Time remaining in seconds to count down from
 * @param {Function} [options.onStart] - Callback triggered when timer starts
 * @param {Function} [options.onPause] - Callback triggered when timer pauses
 * @param {Function} [options.onEnd] - Callback triggered when timer reaches 0
 * @returns {Object} Timer state and controls
 */
const useTimer = ({ timeRemaining: initialTime, onStart, onPause, onEnd }) => {
  const [timeRemaining, setTimeRemaining] = useState(initialTime)
  const [isRunning, setIsRunning] = useState(false)

  const intervalRef = useRef(null)
  const onEndRef = useRef(onEnd)
  const onStartRef = useRef(onStart)
  const onPauseRef = useRef(onPause)

  const resetInterval = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
  }, [])

  // Keep callback refs up to date
  useEffect(() => {
    onEndRef.current = onEnd
  }, [onEnd])

  useEffect(() => {
    onStartRef.current = onStart
  }, [onStart])

  useEffect(() => {
    onPauseRef.current = onPause
  }, [onPause])

  // Handle countdown logic
  useEffect(() => {
    if (isRunning && timeRemaining > 0) {
      intervalRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            resetInterval()
            setIsRunning(false)
            if (onEndRef.current) {
              onEndRef.current()
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => resetInterval()
  }, [isRunning, timeRemaining, resetInterval])

  const start = useCallback(() => {
    if (!isRunning && timeRemaining > 0) {
      setIsRunning(true)
      if (onStartRef.current) {
        onStartRef.current()
      }
    }
  }, [isRunning, timeRemaining])

  const pause = useCallback(() => {
    if (isRunning) {
      setIsRunning(false)
      resetInterval()
      if (onPauseRef.current) {
        onPauseRef.current()
      }
    }
  }, [isRunning, resetInterval])

  const end = useCallback(() => {
    setIsRunning(false)
    setTimeRemaining(0)
    resetInterval()
    if (onEndRef.current) {
      onEndRef.current()
    }
  }, [resetInterval])

  return {
    timeRemaining,
    isRunning,
    start,
    pause,
    end,
  }
}

export default useTimer
